/* Recover Shrine cart rendering when Shopify committed a change but the
 * browser-side fetch chain lost the response. Never retries the mutation. */
(function () {
  'use strict';

  if (typeof window.fetch !== 'function' || window.__scCartMutationRecovery) return;
  window.__scCartMutationRecovery = true;

  var upstreamFetch = window.fetch;
  /* Deliberately has no elapsed-time expiry. Until a mutation promise settles,
   * its server commit status is unknown; clearing it on a clock could let a
   * later lost response reconcile against state produced by the wrong request.
   * This is a fixed-size fail-closed guard, not a retry or refresh queue. */
  var activeCartMutationBatch = null;

  window.fetch = function (resource, init) {
    var mutation = readChangeMutation(resource, init);
    var mutationBatch = isCartMutationRequest(resource, init)
      ? beginCartMutationBatch()
      : null;
    var responsePromise;
    try {
      responsePromise = upstreamFetch.apply(this, arguments);
    } catch (error) {
      finishCartMutationBatch(mutationBatch);
      throw error;
    }
    if (!mutationBatch) return responsePromise;
    if (!mutation) {
      responsePromise.then(function () {
        finishCartMutationBatch(mutationBatch);
      }, function () {
        finishCartMutationBatch(mutationBatch);
      });
      return responsePromise;
    }

    var handledPromise = responsePromise.catch(function (error) {
      if (!isRecoverableNetworkFailure(error) || !mutation.key) throw error;
      if (mutationBatch.overlapped) throw error;

      return recoverCommittedMutation(mutation, mutationBatch).catch(function () {
        /* Preserve Shrine's native error path when the intended state cannot
         * be proven. Retrying here could apply the quantity change twice. */
        throw error;
      });
    });
    handledPromise.then(function () {
      finishCartMutationBatch(mutationBatch);
    }, function () {
      finishCartMutationBatch(mutationBatch);
    });
    return handledPromise;
  };

  function isCartMutationRequest(resource, init) {
    var url = typeof resource === 'string'
      ? resource
      : (typeof Request !== 'undefined' && resource instanceof Request ? resource.url : '');
    var method = String((init && init.method)
      || (typeof Request !== 'undefined' && resource instanceof Request && resource.method)
      || 'GET').toUpperCase();
    return method === 'POST'
      && /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url);
  }

  function beginCartMutationBatch() {
    if (!activeCartMutationBatch) {
      activeCartMutationBatch = { pending: 0, overlapped: false };
    } else if (activeCartMutationBatch.pending > 0) {
      activeCartMutationBatch.overlapped = true;
    }
    activeCartMutationBatch.pending += 1;
    // Read by sc-cart-protection-toggle: its line-index mutations wait for
    // in-flight theme cart mutations so 1-based indexes cannot shift under it.
    window.__scCartMutationPending = true;
    return activeCartMutationBatch;
  }

  function finishCartMutationBatch(batch) {
    if (!batch || batch.pending <= 0) return;
    batch.pending -= 1;
    if (batch.pending === 0 && activeCartMutationBatch === batch) {
      activeCartMutationBatch = null;
    }
    window.__scCartMutationPending = activeCartMutationBatch !== null;
  }

  function readChangeMutation(resource, init) {
    var url = typeof resource === 'string'
      ? resource
      : (typeof Request !== 'undefined' && resource instanceof Request ? resource.url : '');
    var method = String((init && init.method)
      || (typeof Request !== 'undefined' && resource instanceof Request && resource.method)
      || 'GET').toUpperCase();
    if (method !== 'POST' || !/\/cart\/change(?:\.js)?(?:\?|$)/.test(url)) return null;

    var body = init && init.body;
    if (typeof body !== 'string') return null;

    try {
      var payload = JSON.parse(body);
      var line = parseInt(payload.line, 10);
      var quantity = parseInt(payload.quantity, 10);
      if (!(line > 0) || !(quantity >= 0)) return null;

      var sections = Array.isArray(payload.sections) ? payload.sections.filter(isNonEmptyString) : [];
      var rowContext = findMutationRow(line, sections);
      var row = rowContext.row;
      var key = row && row.getAttribute
        ? row.getAttribute('data-cart-item-key')
        : null;
      var quantityInput = row && typeof row.querySelector === 'function'
        ? row.querySelector('[data-quantity-variant-id]')
        : null;
      var variantId = quantityInput && quantityInput.getAttribute
        ? quantityInput.getAttribute('data-quantity-variant-id')
        : null;
      var nextRow = getSurfaceRow(line + 1, rowContext.surface);
      var nextKey = nextRow && nextRow.getAttribute
        ? nextRow.getAttribute('data-cart-item-key')
        : null;

      return {
        line: line,
        lineCount: countCartLines(rowContext.surface),
        key: key || '',
        nextKey: nextKey || '',
        variantId: variantId == null ? '' : String(variantId),
        quantity: quantity,
        sections: sections,
        sectionsUrl: typeof payload.sections_url === 'string' ? payload.sections_url : ''
      };
    } catch (error) {
      return null;
    }
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
  }

  function findMutationRow(line, sections) {
    var pageRow = document.getElementById('CartItem-' + line);
    var drawerRow = document.getElementById('CartDrawer-Item-' + line);
    var requestsDrawer = sections.indexOf('cart-drawer') !== -1;
    var requestsPage = sections.indexOf('main-cart-items') !== -1;

    if (requestsDrawer && !requestsPage) {
      return drawerRow
        ? { row: drawerRow, surface: 'drawer' }
        : { row: pageRow, surface: 'page' };
    }
    if (requestsPage && !requestsDrawer) {
      return pageRow
        ? { row: pageRow, surface: 'page' }
        : { row: drawerRow, surface: 'drawer' };
    }
    return pageRow
      ? { row: pageRow, surface: 'page' }
      : { row: drawerRow, surface: 'drawer' };
  }

  function countCartLines(surface) {
    if (typeof document.querySelectorAll !== 'function') return 0;
    var prefix = surface === 'drawer' ? 'CartDrawer-Item-' : 'CartItem-';
    return document.querySelectorAll('[id^="' + prefix + '"]').length;
  }

  function getSurfaceRow(line, surface) {
    var prefix = surface === 'drawer' ? 'CartDrawer-Item-' : 'CartItem-';
    return document.getElementById(prefix + line);
  }

  function isRecoverableNetworkFailure(error) {
    return !!error && error.name === 'TypeError';
  }

  function recoverCommittedMutation(mutation, mutationBatch) {
    return fetchJson(getShopifyRoute('cart.js'), {
      Accept: 'application/json'
    }).then(function (cart) {
      if (mutationBatch.overlapped) {
        throw new Error('Cart mutation recovery overlapped another mutation');
      }
      if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') {
        throw new Error('Cart reconciliation response is invalid');
      }

      var item = cart.items.find(function (candidate) {
        return candidate && candidate.key === mutation.key;
      });
      var originalLineItem = cart.items[mutation.line - 1];
      var originalLineVariantMatches = mutation.variantId
        && originalLineItem
        && String(originalLineItem.variant_id) === mutation.variantId;
      var rotatedKeyItemMatches = !item
        && originalLineVariantMatches
        && Number(originalLineItem.quantity) === mutation.quantity;
      var lineCountDropped = mutation.lineCount > 0
        && cart.items.length < mutation.lineCount;
      var nextLineShifted = mutation.nextKey
        && originalLineItem
        && originalLineItem.key === mutation.nextKey;
      var removalIdentityProven = mutation.variantId
        && (!originalLineVariantMatches || nextLineShifted);
      var committed = mutation.quantity === 0
        ? !item && lineCountDropped && removalIdentityProven
        : (!!item && Number(item.quantity) === mutation.quantity) || rotatedKeyItemMatches;
      if (!committed) throw new Error('Cart mutation was not committed');

      return fetchSections(mutation).then(function (sections) {
        if (mutationBatch.overlapped) {
          throw new Error('Cart mutation recovery overlapped another mutation');
        }
        cart.sections = sections;
        dispatchRecoveryEvent(mutation, cart);
        return new Response(JSON.stringify(cart), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      });
    });
  }

  function fetchJson(url, headers) {
    var options = { credentials: 'same-origin' };
    if (headers) options.headers = headers;

    return upstreamFetch.call(window, url, options).then(function (response) {
      if (!response.ok) throw new Error('Cart recovery request failed');
      return response.json();
    });
  }

  function fetchSections(mutation) {
    if (!mutation.sections.length) throw new Error('Cart recovery sections are unavailable');

    var base = mutation.sectionsUrl
      || (window.location && window.location.pathname)
      || getShopifyRoute('cart');
    var url = new URL(base, window.location.origin);
    url.searchParams.set('sections', mutation.sections.join(','));

    /* Product routes treat `Accept: application/json` as the Product Ajax API
     * and return `{ product: ... }`; Section Rendering must negotiate normally. */
    return fetchJson(url.pathname + url.search).then(function (sections) {
      var complete = sections && mutation.sections.every(function (id) {
        return typeof sections[id] === 'string' && sections[id].length > 0;
      });
      if (!complete) throw new Error('Cart recovery section response is incomplete');
      return sections;
    });
  }

  function getShopifyRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(path || '').replace(/^\//, '');
  }

  function dispatchRecoveryEvent(mutation, cart) {
    if (typeof window.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('sc:cart-mutation-recovered', {
      detail: {
        key: mutation.key,
        quantity: mutation.quantity,
        itemCount: cart.item_count
      }
    }));
  }
})();
